import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilValue } from "recoil";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import { useGetOfficeDarkReason } from "../hooks/useGetOfficeDarkReason";
import { accountAtom } from "../../../Login/atoms/AccountAtom";
import { officeAddState } from "../atoms/atoms";

const OfficeDates = ({ disableFields }) => {
  const formMethods = useFormContext();

  const user = useRecoilValue(accountAtom);

  const franchiseDarkReason = [
    "Capital Issues",
    "Franchise Owner Abandonment",
    "Inclement Weather",
    "IT/Network Issues",
    "Mandated",
    "Other",
    "Pending Confie Takeover of Lease",
    "Pending FA Termination",
    "Pending Refranchising",
  ];

  const franchiseClosedReason = [
    "Capital Issues",
    "Franchise Owner Abandonment",
    "Mandated",
    "Other",
    "Pending Confie Takeover of Lease",
    "Pending Refranchising",
  ];

  const officeDarkReason = useGetOfficeDarkReason()?.sort((a, b) =>
    a.label?.toLowerCase() > b.label?.toLowerCase() ? 1 : -1,
  );

  const darkReasonOpts = officeDarkReason.filter((x) => {
    if (
      user.isFranchise &&
      formMethods.watch("officeStatus") === "Dark - Franchise" &&
      formMethods.watch("officeType")?.toLowerCase() === "franchise"
    ) {
      return franchiseDarkReason?.includes(x.label);
    }
    return true;
  });

  const closedReasonOpts = officeDarkReason.filter((x) => {
    if (
      user.isFranchise &&
      formMethods
        .watch("officeStatus")
        ?.toLowerCase()
        ?.includes("closed - fa") &&
      formMethods.watch("officeType")?.toLowerCase() === "franchise"
    ) {
      return franchiseClosedReason?.includes(x.label);
    }
    return true;
  });

  const isAddState = useRecoilValue(officeAddState);

  const isDateOpenRequired = formMethods
    .watch("officeStatus")
    ?.toLowerCase()
    ?.includes("open");

  const isDateCloseRequired =
    formMethods.watch("officeStatus")?.toLowerCase()?.includes("close") ||
    formMethods.watch("officeStatus")?.toLowerCase()?.includes("refranchised");

  const darkStatus = formMethods
    .watch("officeStatus")
    ?.toLowerCase()
    ?.includes("dark");

  const closedReasons =
    formMethods.watch("officeStatus")?.toLowerCase()?.includes("closed - fa") &&
    formMethods.watch("officeType")?.toLowerCase() === "franchise";

  const currentStatus = formMethods.watch("currentOfficeStatus")?.toLowerCase();
  const isReOpenDate =
    currentStatus?.includes("dark") ||
    currentStatus?.includes("temporary") ||
    currentStatus?.includes("close");
  const maxExpectedClosureLength = 50;
  const expectedClosureLength =
    formMethods.watch("expectedLengthOfClosureReopenDate") || "";
  const isExpectedClosureLengthOverLimit =
    expectedClosureLength.length > maxExpectedClosureLength;

  return (
    <div className="m-2 bg-white box-shadow rounded-lg h-full p-1 pb-4">
      <div className="text-quinary font-bold font-sans py-2 px-4">Dates</div>
      <div className="grid grid-cols-1 md:grid-cols-2 px-4 gap-12">
        <div className="space-y-1 pb-4">
          <TextInputLabelHookForm
            name="originalOpenedDate"
            label="Original Opened Date"
            type="date"
            register={formMethods.register("originalOpenedDate", {})}
            disabled={disableFields}
          />
          <TextInputLabelHookForm
            name="officeOpenedDate"
            label="Opened Date"
            type="date"
            required={isDateOpenRequired}
            disabled={disableFields}
            className={
              formMethods.formState.errors.officeOpenedDate &&
              formMethods.formState.errors.officeOpenedDate.message &&
              "border-2 border-red-500 "
            }
            register={formMethods.register("officeOpenedDate", {
              required: {
                value: isDateOpenRequired,
                message: "Required Field",
              },
            })}
          />
          <TextInputLabelHookForm
            name="acquisitionDate"
            label="Acquisition Date"
            type="date"
            required={
              formMethods.watch("officeStatusDetails")?.toLowerCase() ===
                "acquisition" && isAddState
                ? true
                : false
            }
            disabled={disableFields}
            register={formMethods.register("acquisitionDate", {
              required: {
                value:
                  formMethods.watch("officeStatusDetails")?.toLowerCase() ===
                    "acquisition" && isAddState,
                message: "Required Field",
              },
            })}
            className={
              formMethods.formState.errors.acquisitionDate &&
              formMethods.formState.errors.acquisitionDate.message &&
              "border-2 border-red-500 col-span-2 "
            }
          />
          <TextInputLabelHookForm
            name="relocationDate"
            label="Relocation Date"
            type="date"
            required={false}
            register={formMethods.register("relocationDate", {})}
            disabled={disableFields}
          />
        </div>
        <div className="space-y-1 pb-4">
          <TextInputLabelHookForm
            name="officeClosedDate"
            label="Closed Date"
            type="date"
            min={formMethods.watch("officeOpenedDate")}
            required={isDateCloseRequired}
            disabled={disableFields}
            className={
              formMethods.formState.errors.reOpenDate &&
              formMethods.formState.errors.reOpenDate.message &&
              "border-2 border-red-500 "
            }
            register={formMethods.register("officeClosedDate", {
              required: {
                value: isDateCloseRequired,
                message: "Required Field",
              },
            })}
          />
          {darkStatus && (
            <TextInputLabelHookForm
              name="darkReasonEffectiveDate"
              label="Dark Reason Effective Date"
              type="date"
              required={darkStatus}
              disabled={disableFields}
              min={formMethods.watch("officeOpenedDate")}
              className={
                formMethods.formState.errors.darkReasonEffectiveDate &&
                formMethods.formState.errors.darkReasonEffectiveDate.message &&
                "border-2 border-red-500 "
              }
              register={formMethods.register("darkReasonEffectiveDate", {
                required: {
                  value: true,
                  message: "Required Field",
                },
              })}
            />
          )}
          {darkStatus && (
            <Controller
              control={formMethods.control}
              name="officeDarkReason"
              rules={{ required: darkStatus }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => {
                    field.onChange(e ? e.value : null);
                  }}
                  value={field.value}
                  options={darkReasonOpts}
                  label="Dark Status Reason"
                  className={
                    formMethods.formState.errors.officeDarkReason &&
                    "border-2 border-red-500 "
                  }
                  required={darkStatus}
                  isDisabled={disableFields}
                />
              )}
            />
          )}
          {closedReasons && (
            <Controller
              control={formMethods.control}
              name="closedReason"
              rules={{ required: closedReasons }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => {
                    field.onChange(e ? e.value : null);
                  }}
                  value={field.value}
                  options={closedReasonOpts}
                  label="Closed Status Reason"
                  className={
                    formMethods.formState.errors.closedReason &&
                    "border-2 border-red-500 "
                  }
                  required={closedReasons}
                  isDisabled={disableFields}
                />
              )}
            />
          )}

          <TextInputLabelHookForm
            name="expectedLengthOfClosureReopenDate"
            label="Expected length of closure/reopen date"
            type="text"
            className={
              isExpectedClosureLengthOverLimit
                ? "border-2 border-red-500 "
                : undefined
            }
            error={
              isExpectedClosureLengthOverLimit
                ? `Cannot exceed ${maxExpectedClosureLength} characters`
                : undefined
            }
            register={formMethods.register(
              "expectedLengthOfClosureReopenDate",
              {
                maxLength: {
                  value: maxExpectedClosureLength,
                  message: `Cannot exceed ${maxExpectedClosureLength} characters`,
                },
              },
            )}
            disabled={disableFields}
          />

          {isDateOpenRequired &&
            formMethods.formState.dirtyFields.officeStatusId &&
            isReOpenDate && (
              <TextInputLabelHookForm
                name="reOpenDate"
                label="Re-Open Date"
                type="date"
                required={isDateOpenRequired}
                disabled={disableFields}
                min={formMethods.watch("officeClosedDate")}
                className={
                  formMethods.formState.errors.reOpenDate &&
                  formMethods.formState.errors.reOpenDate.message &&
                  "border-2 border-red-500 "
                }
                register={formMethods.register("reOpenDate", {
                  required: {
                    value: true,
                    message: "Required Field",
                  },
                })}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default OfficeDates;
